import logger from '../../../services/logger';
import type {FollowUpChannel} from '../../../store/features/calls/callsService';
import type {InsightsEntry, InsightsTab} from './useInsightsDrawerParams';

export type SeekVia = 'row' | 'timestamp' | 'citation' | 'topic' | 'waveform' | 'timeline' | 'keyboard';
export type CopyKind = 'summary' | 'transcript' | 'ask' | 'follow_up' | 'link';
export type GenerationMode = 'generate' | 'upgrade' | 'transcript';
export type TabChangeVia = 'click' | 'keyboard' | 'citation' | 'topic';

export const logDrawerOpened = (context: {callId: number; entry: InsightsEntry; tab: InsightsTab; hasTranscript: boolean; insightsVersion: number | null}): void => {
    logger.info('Call insights opened', {
        source: 'call_insights',
        event: 'drawer_opened',
        callId: String(context.callId),
        entry: context.entry,
        tab: context.tab,
        hasTranscript: context.hasTranscript,
        insightsVersion: context.insightsVersion,
    });
};

export const logDrawerClosed = (context: {callId: number; tab: InsightsTab}): void => {
    logger.info('Call insights closed', {source: 'call_insights', event: 'drawer_closed', callId: String(context.callId), tab: context.tab});
};

export const logTabChanged = (context: {tab: InsightsTab; via: TabChangeVia}): void => {
    logger.info('Call insights tab changed', {source: 'call_insights', event: 'tab_changed', tab: context.tab, via: context.via});
};

export const logPlaybackSeek = (seekVia: SeekVia): void => {
    logger.info('Call insights playback seek', {source: 'call_insights', event: 'playback_seek', via: seekVia});
};

export const logTranscriptSearch = (matches: number): void => {
    logger.info('Call insights transcript searched', {source: 'call_insights', event: 'transcript_search', matchCount: matches});
};

export const logContentCopied = (copyKind: CopyKind): void => {
    logger.info('Call insights content copied', {source: 'call_insights', event: 'content_copied', kind: copyKind});
};

export const logTranscriptExported = (): void => {
    logger.info('Call insights transcript exported', {source: 'call_insights', event: 'transcript_exported'});
};

export const logGenerationRequested = (context: {callId: number; mode: GenerationMode}): void => {
    logger.info('Call insights generation requested', {source: 'call_insights', event: 'insights_generation_requested', callId: String(context.callId), mode: context.mode});
};

export const logGenerationSucceeded = (context: {callId: number; mode: GenerationMode}): void => {
    logger.info('Call insights generation succeeded', {source: 'call_insights', event: 'insights_generation_succeeded', callId: String(context.callId), mode: context.mode});
};

export const logGenerationFailed = (context: {callId: number; mode: GenerationMode; status: number | string | null}): void => {
    logger.warn('Call insights generation failed', {
        source: 'call_insights',
        event: 'insights_generation_failed',
        callId: String(context.callId),
        mode: context.mode,
        status: context.status,
    });
};

export const logCalendarExported = (calendar: 'ics' | 'google'): void => {
    logger.info('Call insights meeting exported', {source: 'call_insights', event: 'calendar_event_exported', target: calendar});
};

export const logContactSuggestionOpened = (panelMode: 'create' | 'complete'): void => {
    logger.info('Call insights contact suggestion opened', {source: 'call_insights', event: 'contact_suggestion_opened', mode: panelMode});
};

export const logFollowUpDrafted = (context: {callId: number; channel: FollowUpChannel; status: number | string}): void => {
    logger.info('Call insights follow-up drafted', {
        source: 'call_insights',
        event: 'follow_up_drafted',
        callId: String(context.callId),
        channel: context.channel,
        status: context.status,
    });
};

export const logFollowUpMailAppOpened = (): void => {
    logger.info('Call insights follow-up opened in mail app', {source: 'call_insights', event: 'follow_up_mail_app_opened'});
};

export const logQuestionAsked = (context: {callId: number; status: number | string; answerable: boolean | null; citationCount: number}): void => {
    logger.info('Call insights question asked', {
        source: 'call_insights',
        event: 'question_asked',
        callId: String(context.callId),
        status: context.status,
        answerable: context.answerable,
        citationCount: context.citationCount,
    });
};

export const logWaveformDecodeFailed = (): void => {
    logger.warn('Call insights waveform decode failed', {source: 'call_insights', event: 'waveform_decode_failed'});
};
